import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Folder } from '@mui/icons-material';
import { GruppoProgetti } from '../types/gruppo';
import GruppoProgettiDialog from '../components/GruppoProgettiDialog';
import * as api from '../services/api';

const GruppiProgettiPage: React.FC = () => {
  const [gruppi, setGruppi] = useState<GruppoProgetti[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedGruppo, setSelectedGruppo] = useState<GruppoProgetti | null>(null);

  useEffect(() => {
    fetchGruppi();
  }, []);

  const fetchGruppi = async () => {
    try {
      setLoading(true);
      setError(null);
      const gruppi = await api.getGruppiProgetti();
      setGruppi(gruppi);
    } catch (err: any) {
      console.error('Errore caricamento gruppi:', err);
      setError(err.response?.data?.error || 'Errore caricamento gruppi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedGruppo(null);
    setDialogOpen(true);
  };

  const handleEdit = (gruppo: GruppoProgetti) => {
    setSelectedGruppo(gruppo);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo gruppo?')) {
      return;
    }

    try {
      await api.deleteGruppoProgetti(id);
      await fetchGruppi();
    } catch (err: any) {
      console.error('Errore eliminazione gruppo:', err);
      setError(err.response?.data?.error || 'Errore eliminazione gruppo');
    }
  };

  const handleDialogClose = (saved: boolean) => {
    setDialogOpen(false);
    setSelectedGruppo(null);
    if (saved) {
      fetchGruppi();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gruppi Progetti</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Nuovo Gruppo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {gruppi.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Nessun gruppo creato. Clicca su "Nuovo Gruppo" per iniziare.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {gruppi.map((gruppo) => (
            <Grid item xs={12} sm={6} md={4} key={gruppo.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: gruppo.colore ? `4px solid ${gruppo.colore}` : 'none',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Folder sx={{ mr: 1, color: gruppo.colore || 'text.secondary' }} />
                    <Typography variant="h6" component="div">
                      {gruppo.nome}
                    </Typography>
                  </Box>

                  {gruppo.descrizione && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {gruppo.descrizione}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${gruppo.progetti.length} progetti`}
                      size="small"
                      color={gruppo.progetti.length > 0 ? 'primary' : 'default'}
                    />
                    {gruppo.colore && (
                      <Chip
                        label="Colore"
                        size="small"
                        sx={{
                          bgcolor: gruppo.colore,
                          color: '#fff',
                        }}
                      />
                    )}
                  </Box>

                  {gruppo.progetti.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Progetti nel gruppo:
                      </Typography>
                      <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                        {gruppo.progetti.map((progetto) => (
                          <Typography key={progetto.id} variant="caption" display="block" sx={{ pl: 1 }}>
                            • {progetto.id_hrp ? `[${progetto.id_hrp}] ` : ''}{progetto.titolo}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <Tooltip title="Modifica gruppo">
                    <IconButton size="small" onClick={() => handleEdit(gruppo)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Elimina gruppo">
                    <IconButton size="small" color="error" onClick={() => handleDelete(gruppo.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <GruppoProgettiDialog
        open={dialogOpen}
        gruppo={selectedGruppo}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default GruppiProgettiPage;
